/**
 * Dashboard banner nudging the primary user to complete outstanding
 * trusted-party handshakes (medium-term #2, docs/KEY_WRAPPING_DESIGN.md
 * §7.1 option A).
 *
 * A handshake is "pending" when an invitee accepted + published their
 * wrapping public key but the primary hasn't yet sealed the master key
 * for them. Tapping the banner routes to PendingConnectionsScreen where
 * they can finish each one in a tap.
 *
 * Hidden while loading, hidden when there's nothing pending. Refreshes
 * on focus so it disappears after the user completes everything.
 */
import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { ms } from '../utils/responsive';
import { vaultService } from '../services/vaultService';
import { createLogger } from '../utils/logger';

const logger = createLogger('PendingConnectionsBanner');

export const PendingConnectionsBanner: React.FC = () => {
  const navigation = useNavigation<any>();
  const [count, setCount] = useState<number | null>(null);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      (async () => {
        try {
          const list = await vaultService.listPendingTrustedPartyHandshakes();
          if (cancelled) return;
          setCount(list.length);
        } catch (err) {
          // Fail silent — the banner just stays hidden on error. This is a
          // nudge surface; network hiccups shouldn't yell at the user.
          logger.debug('Pending handshakes probe failed:', err);
          if (!cancelled) setCount(0);
        }
      })();
      return () => {
        cancelled = true;
      };
    }, []),
  );

  if (count === null || count === 0) return null;

  const title =
    count === 1
      ? '1 pending connection'
      : `${count} pending connections`;
  const subtitle =
    'Finalize your successor / keyholder set-ups so they can decrypt your vault at the right time.';

  return (
    <TouchableOpacity
      style={styles.banner}
      onPress={() =>
        navigation.navigate('Profile', { screen: 'PendingConnections' })
      }
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      <View style={{ flex: 1 }}>
        <Text style={styles.eyebrow}>Action needed</Text>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>
      </View>
      <Text style={styles.arrow}>→</Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1C2C4F',
    marginHorizontal: ms(12),
    marginTop: ms(10),
    marginBottom: ms(4),
    padding: ms(14),
    borderRadius: ms(12),
  },
  eyebrow: {
    fontSize: ms(10, 0.3),
    fontWeight: '700',
    color: '#F6C867',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: ms(3),
  },
  title: {
    fontSize: ms(15, 0.3),
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: ms(2),
  },
  subtitle: {
    fontSize: ms(12, 0.3),
    color: '#C6D2E3',
    lineHeight: ms(17, 0.3),
  },
  arrow: {
    fontSize: ms(22, 0.3),
    color: '#F6C867',
    marginLeft: ms(10),
    fontWeight: '700',
  },
});
